import { useState } from 'react'
import { PlusIcon, XMarkIcon } from '@heroicons/react/24/outline'

const MAX_QUESTIONS = 5
const MAX_QUESTION_LENGTH = 100

export default function SuggestedQuestionsEditor({ questions = [], onChange }) {
  const [isAdding, setIsAdding] = useState(false)
  const [newQuestion, setNewQuestion] = useState('')

  const handleAdd = () => {
    const trimmed = newQuestion.trim()
    if (trimmed && questions.length < MAX_QUESTIONS) {
      onChange([...questions, trimmed])
      setNewQuestion('')
      setIsAdding(false)
    }
  }

  const handleRemove = (index) => {
    onChange(questions.filter((_, i) => i !== index))
  }

  const handleUpdate = (index, value) => {
    const updated = [...questions]
    updated[index] = value
    onChange(updated)
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleAdd()
    } else if (e.key === 'Escape') {
      setIsAdding(false)
      setNewQuestion('')
    }
  }

  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-1.5">
        Suggested Questions
      </label>
      <p className="text-xs text-slate-500 mb-3">
        These appear as quick-action chips in the chat widget (max {MAX_QUESTIONS}).
      </p>

      <div className="space-y-2">
        {questions.map((question, index) => (
          <div key={index} className="flex items-center space-x-2 group">
            <input
              type="text"
              value={question}
              onChange={(e) => handleUpdate(index, e.target.value)}
              maxLength={MAX_QUESTION_LENGTH}
              className="flex-1 px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
            />
            <button
              onClick={() => handleRemove(index)}
              className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
              title="Remove question"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        ))}

        {isAdding && (
          <div className="flex items-center space-x-2">
            <input
              type="text"
              value={newQuestion}
              onChange={(e) => setNewQuestion(e.target.value)}
              onKeyDown={handleKeyDown}
              maxLength={MAX_QUESTION_LENGTH}
              placeholder="Type a suggested question..."
              autoFocus
              className="flex-1 px-3 py-2 border border-indigo-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
            />
            <button
              onClick={handleAdd}
              disabled={!newQuestion.trim()}
              className="px-3 py-2 bg-indigo-600 text-white text-sm font-medium rounded-xl hover:bg-indigo-700 disabled:opacity-50 transition-colors"
            >
              Add
            </button>
            <button
              onClick={() => { setIsAdding(false); setNewQuestion('') }}
              className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg transition-colors"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        )}

        {!isAdding && questions.length < MAX_QUESTIONS && (
          <button
            onClick={() => setIsAdding(true)}
            className="flex items-center space-x-1.5 px-3 py-2 text-sm text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 rounded-xl transition-colors"
          >
            <PlusIcon className="h-4 w-4" />
            <span>Add Question</span>
          </button>
        )}

        {questions.length >= MAX_QUESTIONS && !isAdding && (
          <p className="text-xs text-slate-400 mt-1">
            Maximum of {MAX_QUESTIONS} questions reached.
          </p>
        )}
      </div>
    </div>
  )
}
